import React, {useState} from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import {useAuth} from "../authentication/AuthContext";
import {useCreatePracticeSet, useDeletePracticeSet, useGeneratePracticeSet, usePracticeSets} from "./hooks/usePractice";
import AddPracticeSetModal from "./components/AddPracticeSetModal";
import {ChevronRight, GraduationCap, LayoutGrid, List, Plus, Trash2} from "lucide-react";
import {useNavigate} from "react-router-dom";

function getQuestionSummary(practiceSet) {
    const items = practiceSet.practiceItems ?? [];
    if (items.length === 0) return 'No questions';

    const counts = items.reduce((acc, item) => {
        const key = item.questionType ?? 'MULTIPLE_CHOICE';
        acc[key] = (acc[key] ?? 0) + 1;
        return acc;
    }, {});

    return Object.entries(counts)
        .map(([key, value]) => `${value} ${key.replace('_', ' ').toLowerCase()}`)
        .join(' • ');
}

function PracticeCard({practiceSet, isSelected, onToggleSelection, isDeleting, isSelectionMode}) {
    const navigate = useNavigate();
    const itemCount = practiceSet.practiceItems?.length ?? 0;

    return (
        <div className="relative">
            {isSelectionMode && <input type="checkbox" checked={isSelected} onChange={onToggleSelection} disabled={isDeleting} aria-label={`Select ${practiceSet.title || 'Untitled practice set'}`} className="absolute left-4 top-5 z-10 h-4 w-4 cursor-pointer rounded border-[var(--border-strong)] bg-[var(--surface-deep)] text-[var(--brand-coral)] focus:ring-[var(--brand-coral)]" />}
            <button
                type="button"
                onClick={() => navigate(`/practice/${practiceSet.id}`)}
                className={`group flex w-full flex-col gap-3 rounded-2xl border bg-[var(--surface)] py-5 pr-5 text-left transition-all duration-200 hover:border-[var(--brand-coral)]/50 hover:shadow-lg hover:shadow-[var(--brand-coral)]/5 ${isSelectionMode ? 'pl-12' : 'pl-5'} ${isSelected ? 'border-[var(--brand-coral)]/60 ring-1 ring-[var(--brand-coral)]/30' : 'border-[var(--border-color)]'}`}
            >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <h3 className="truncate text-base font-semibold text-[var(--text-primary)] group-hover:text-[var(--brand-coral)]">
                        {practiceSet.title || 'Untitled practice set'}
                    </h3>
                    {practiceSet.description && (
                        <p className="mt-1 line-clamp-2 text-sm text-[var(--text-muted)]">
                            {practiceSet.description}
                        </p>
                    )}
                </div>
                <ChevronRight size={16} className="mt-0.5 shrink-0 text-[var(--text-muted)] transition-colors group-hover:text-[var(--brand-coral)]" />
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="rounded-full border border-[var(--brand-coral)]/20 bg-[var(--brand-coral)]/10 px-2.5 py-1 font-medium text-[var(--brand-coral)]">
                    {itemCount} question{itemCount === 1 ? '' : 's'}
                </span>
                <span className="rounded-full border border-[var(--border-color)] bg-[var(--surface-hover)] px-2.5 py-1 text-[var(--text-secondary)]">
                    {getQuestionSummary(practiceSet)}
                </span>
                {practiceSet.language && (
                    <span className="rounded-full border border-[var(--border-color)] bg-[var(--surface-hover)] px-2.5 py-1 text-[var(--text-secondary)]">
                        {practiceSet.language}
                    </span>
                )}
            </div>
            </button>
        </div>
    );
}

function PracticeListRow({practiceSet, isSelected, onToggleSelection, isDeleting, isSelectionMode}) {
    const navigate = useNavigate();
    const itemCount = practiceSet.practiceItems?.length ?? 0;

    return (
        <div className="relative">
            {isSelectionMode && <input type="checkbox" checked={isSelected} onChange={onToggleSelection} disabled={isDeleting} aria-label={`Select ${practiceSet.title || 'Untitled practice set'}`} className="absolute left-4 top-1/2 z-10 h-4 w-4 -translate-y-1/2 cursor-pointer rounded border-[var(--border-strong)] bg-[var(--surface-deep)] text-[var(--brand-coral)] focus:ring-[var(--brand-coral)]" />}
            <button
                type="button"
                onClick={() => navigate(`/practice/${practiceSet.id}`)}
                className={`group flex w-full items-center gap-4 rounded-xl border bg-[var(--surface)] py-3 pr-4 text-left transition-all duration-200 hover:border-[var(--brand-coral)]/50 hover:shadow-lg hover:shadow-[var(--brand-coral)]/5 ${isSelectionMode ? 'pl-12' : 'pl-4'} ${isSelected ? 'border-[var(--brand-coral)]/60 ring-1 ring-[var(--brand-coral)]/30' : 'border-[var(--border-color)]'}`}
            >
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm font-semibold text-[var(--text-primary)] transition-colors group-hover:text-[var(--brand-coral)]">
                        {practiceSet.title || 'Untitled practice set'}
                    </span>
                    <span className="shrink-0 rounded-full border border-[var(--brand-coral)]/20 bg-[var(--brand-coral)]/10 px-2 py-0.5 text-xs font-medium text-[var(--brand-coral)]">
                        {itemCount} question{itemCount === 1 ? '' : 's'}
                    </span>
                    {practiceSet.language && (
                        <span className="shrink-0 rounded-full border border-[var(--border-color)] bg-[var(--surface-hover)] px-2 py-0.5 text-xs text-[var(--text-secondary)]">
                            {practiceSet.language}
                        </span>
                    )}
                </div>
                <p className="mt-0.5 truncate text-xs text-[var(--text-muted)]">
                    {practiceSet.description || getQuestionSummary(practiceSet)}
                </p>
            </div>
            <span className="hidden max-w-xs truncate text-xs text-[var(--text-muted)] md:block">
                {getQuestionSummary(practiceSet)}
            </span>
            <ChevronRight size={15} className="shrink-0 text-[var(--text-muted)] transition-colors group-hover:text-[var(--brand-coral)]" />
            </button>
        </div>
    );
}

export default function Practice() {
    const {authToken, user} = useAuth();
    const [showModal, setShowModal] = useState(false);
    const [viewMode, setViewMode] = useState('grid');
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedPracticeSetIds, setSelectedPracticeSetIds] = useState(() => new Set());
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [pageSize, setPageSize] = useState(25);
    const [currentPage, setCurrentPage] = useState(1);
    const {data: practiceSets, isLoading} = usePracticeSets(authToken);
    const {mutateAsync: createPracticeSet, isLoading: isCreating, error: createError, reset: resetCreateError} = useCreatePracticeSet(authToken);
    const {mutateAsync: generatePracticeSet, isLoading: isGenerating, error: generateError, reset: resetGenerateError} = useGeneratePracticeSet(authToken);
    const {mutateAsync: deletePracticeSet, isLoading: isDeleting, error: deleteError, reset: resetDeleteError} = useDeletePracticeSet(authToken);
    const practiceSetList = practiceSets ?? [];
    const totalPages = Math.max(1, Math.ceil(practiceSetList.length / pageSize));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const pageStart = (safeCurrentPage - 1) * pageSize;
    const paginatedPracticeSets = practiceSetList.slice(pageStart, pageStart + pageSize);
    const paginatedIds = paginatedPracticeSets.map((practiceSet) => String(practiceSet.id));
    const allPaginatedSelected = paginatedIds.length > 0 && paginatedIds.every((id) => selectedPracticeSetIds.has(id));

    React.useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const handleSubmit = async (data) => {
        try {
            await createPracticeSet({
                ...data,
                user_id: user?.uid ?? null,
            });
            setShowModal(false);
        } catch {
            // Keep the modal open so the user can correct the form or retry.
        }
    };

    const handleGenerate = async (options) => {
        try {
            await generatePracticeSet(options);
            setShowModal(false);
        } catch {
            // Keep the modal open so the user can adjust the options or retry.
        }
    };

    const toggleSelection = (id) => setSelectedPracticeSetIds((current) => {
        const next = new Set(current);
        const normalizedId = String(id);
        next.has(normalizedId) ? next.delete(normalizedId) : next.add(normalizedId);
        return next;
    });

    const toggleCurrentPageSelection = () => setSelectedPracticeSetIds((current) => {
        const next = new Set(current);
        paginatedIds.forEach((id) => allPaginatedSelected ? next.delete(id) : next.add(id));
        return next;
    });

    const toggleSelectionMode = () => {
        if (isDeleting) return;

        setIsSelectionMode((current) => {
            if (current) setSelectedPracticeSetIds(new Set());
            return !current;
        });
    };

    const handleDeleteSelected = async () => {
        if (selectedPracticeSetIds.size === 0 || isDeleting) return;
        try {
            await Promise.all(Array.from(selectedPracticeSetIds).map((id) => deletePracticeSet(id)));
            setSelectedPracticeSetIds(new Set());
            setIsSelectionMode(false);
            setShowDeleteDialog(false);
        } catch {
            // Keep the selection and dialog open so the user can retry.
        }
    };

    return (
        <div className="flex min-h-screen flex-col bg-[var(--bg)]">
            <Header />
            <div className="flex flex-1 gap-3 bg-[var(--workspace-bg)] px-3 pb-4 pt-[76px] md:gap-4 md:px-4">
                <Sidebar />
                <div className="flex min-w-0 flex-1 flex-col rounded-[10px] bg-[var(--surface)]">
                    <div className="mx-auto flex w-full max-w-7xl flex-1 flex-col px-4 py-8 md:px-6 md:py-10">
                        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
                            <div>
                                <h1 className="flex items-center gap-2 text-2xl font-bold text-[var(--text-primary)]">
                                    <GraduationCap size={24} className="text-[var(--brand-coral)]" />
                                    Practice
                                </h1>
                                <p className="mt-1 text-sm text-[var(--text-muted)]">
                                    {practiceSets?.length ? `${practiceSets.length} practice set(s)` : 'No practice sets yet'}
                                </p>
                            </div>

                            <div className="flex items-center gap-2">
                                {isSelectionMode && (
                                    <button type="button" onClick={() => { resetDeleteError(); setShowDeleteDialog(true); }} disabled={selectedPracticeSetIds.size === 0 || isDeleting} className="inline-flex items-center gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-300 transition hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-40">
                                        <Trash2 size={16} />
                                        {isDeleting ? 'Deleting...' : `Delete${selectedPracticeSetIds.size ? ` (${selectedPracticeSetIds.size})` : ''}`}
                                    </button>
                                )}
                                <button type="button" onClick={toggleSelectionMode} disabled={isDeleting} aria-pressed={isSelectionMode} className="inline-flex items-center rounded-md border border-[var(--border-color)] bg-[var(--surface-deep)] px-4 py-2 text-sm font-medium text-[var(--text-primary)] transition hover:bg-[var(--surface-hover)] disabled:cursor-not-allowed disabled:opacity-40">
                                    {isSelectionMode ? 'Cancel' : 'Select'}
                                </button>
                                <div className="flex items-center rounded-lg border border-[var(--border-color)] bg-[var(--surface)] p-1">
                                    <button
                                        type="button"
                                        onClick={() => setViewMode('grid')}
                                        className={`rounded-md p-1.5 transition-colors ${viewMode === 'grid' ? 'bg-[var(--button-primary-bg)] text-[var(--button-primary-text)]' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}
                                        title="Grid view"
                                        aria-label="Grid view"
                                    >
                                        <LayoutGrid size={15} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setViewMode('list')}
                                        className={`rounded-md p-1.5 transition-colors ${viewMode === 'list' ? 'bg-[var(--button-primary-bg)] text-[var(--button-primary-text)]' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}
                                        title="List view"
                                        aria-label="List view"
                                    >
                                        <List size={15} />
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => { resetCreateError(); resetGenerateError(); setShowModal(true); }}
                                    className="inline-flex items-center gap-2 rounded-xl bg-[var(--button-primary-bg)] px-4 py-2.5 text-sm font-semibold text-[var(--button-primary-text)] shadow-lg transition hover:brightness-105 active:brightness-95"
                                >
                                    <Plus size={16} />
                                    Create Practice
                                </button>
                            </div>
                        </div>

                        {isSelectionMode && !isLoading && practiceSetList.length > 0 && (
                            <div className="mb-3 flex items-center justify-between rounded-lg border border-[var(--border-color)] bg-[var(--surface)] px-4 py-2">
                                <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-[var(--text-muted)]"><input type="checkbox" checked={allPaginatedSelected} onChange={toggleCurrentPageSelection} disabled={isDeleting} className="h-4 w-4 rounded border-[var(--border-strong)] bg-[var(--surface-deep)] text-[var(--brand-coral)] focus:ring-[var(--brand-coral)]" />Select this page</label>
                                {selectedPracticeSetIds.size > 0 && <span className="text-xs text-[var(--brand-coral)]">{selectedPracticeSetIds.size} selected</span>}
                            </div>
                        )}

                        {isLoading && (
                            <div className={viewMode === 'grid' ? 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3' : 'flex flex-col gap-2'}>
                                {new Array(6).fill(null).map((_, index) => (
                                    <div key={`practice-skeleton-${index}`} className={`animate-pulse border border-[var(--border-color)] bg-[var(--surface)] ${viewMode === 'grid' ? 'h-40 rounded-2xl p-5' : 'h-14 rounded-xl px-4'}`} />
                                ))}
                            </div>
                        )}

                        {!isLoading && practiceSets && practiceSets.length > 0 && viewMode === 'grid' && (
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                {paginatedPracticeSets.map((practiceSet) => (
                                    <PracticeCard key={practiceSet.id} practiceSet={practiceSet} isSelected={selectedPracticeSetIds.has(String(practiceSet.id))} onToggleSelection={() => toggleSelection(practiceSet.id)} isDeleting={isDeleting} isSelectionMode={isSelectionMode} />
                                ))}
                            </div>
                        )}

                        {!isLoading && practiceSets && practiceSets.length > 0 && viewMode === 'list' && (
                            <div className="flex flex-col gap-2">
                                {paginatedPracticeSets.map((practiceSet) => (
                                    <PracticeListRow key={practiceSet.id} practiceSet={practiceSet} isSelected={selectedPracticeSetIds.has(String(practiceSet.id))} onToggleSelection={() => toggleSelection(practiceSet.id)} isDeleting={isDeleting} isSelectionMode={isSelectionMode} />
                                ))}
                            </div>
                        )}

                        {!isLoading && practiceSetList.length > 0 && (
                            <div className="mt-auto flex flex-col gap-3 rounded-xl border border-[var(--border-color)] bg-[var(--surface)] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                                    <span>Rows per page</span>
                                    <select
                                        value={pageSize}
                                        onChange={(event) => {
                                            setPageSize(Number(event.target.value));
                                            setCurrentPage(1);
                                        }}
                                        className="rounded-md border border-[var(--border-color)] bg-[var(--surface-deep)] px-2 py-1 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--brand-coral)]"
                                        aria-label="Practice sets per page"
                                    >
                                        {[25, 50, 100].map(size => (
                                            <option key={size} value={size}>{size}</option>
                                        ))}
                                    </select>
                                    <span>
                                        {pageStart + 1}–{Math.min(pageStart + pageSize, practiceSetList.length)} of {practiceSetList.length}
                                    </span>
                                </div>

                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                                        disabled={safeCurrentPage === 1}
                                        className="rounded-md border border-[var(--border-color)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] transition hover:bg-[var(--surface-deep)] disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                        Previous
                                    </button>
                                    <span className="min-w-20 text-center text-xs text-[var(--text-muted)]">
                                        Page {safeCurrentPage} of {totalPages}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
                                        disabled={safeCurrentPage === totalPages}
                                        className="rounded-md border border-[var(--border-color)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] transition hover:bg-[var(--surface-deep)] disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}

                        {!isLoading && (!practiceSets || practiceSets.length === 0) && (
                            <div className="flex flex-col items-center justify-center gap-4 py-28">
                                <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-[var(--border-color)] bg-[var(--surface)]">
                                    <GraduationCap size={28} className="text-[var(--text-muted)]" />
                                </div>
                                <div className="text-center">
                                    <h2 className="text-lg font-semibold text-[var(--text-secondary)]">No practice sets yet</h2>
                                    <p className="mt-1 text-sm text-[var(--text-muted)]">Create your first practice set to get started.</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => { resetCreateError(); resetGenerateError(); setShowModal(true); }}
                                    className="mt-2 inline-flex items-center gap-2 rounded-xl bg-[var(--button-primary-bg)] px-4 py-2.5 text-sm font-semibold text-[var(--button-primary-text)] transition hover:brightness-105"
                                >
                                    <Plus size={16} />
                                    Create Practice
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {showDeleteDialog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 px-4 backdrop-blur-sm" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !isDeleting) setShowDeleteDialog(false); }}>
                    <div role="dialog" aria-modal="true" aria-labelledby="delete-practice-title" className="w-full max-w-md rounded-2xl border border-[var(--border-color)] bg-[var(--surface)] p-6 shadow-2xl shadow-black/50">
                        <div className="flex items-start gap-4"><div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-red-500/30 bg-red-500/10 text-red-300"><Trash2 size={20} /></div><div><h2 id="delete-practice-title" className="text-lg font-semibold text-[var(--text-primary)]">Delete selected practice sets?</h2><p className="mt-2 text-sm leading-6 text-[var(--text-muted)]">{selectedPracticeSetIds.size} selected practice set{selectedPracticeSetIds.size === 1 ? '' : 's'} will be permanently deleted. This action cannot be undone.</p></div></div>
                        {deleteError && <p className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">Some selected practice sets could not be deleted. Please try again.</p>}
                        <div className="mt-6 flex justify-end gap-3"><button type="button" onClick={() => setShowDeleteDialog(false)} disabled={isDeleting} className="rounded-lg border border-[var(--border-strong)] px-4 py-2 text-sm font-medium text-[var(--text-secondary)] transition hover:bg-[var(--surface-hover)] disabled:opacity-50">Cancel</button><button type="button" onClick={handleDeleteSelected} disabled={isDeleting || selectedPracticeSetIds.size === 0} className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-500 disabled:opacity-50"><Trash2 size={16} />{isDeleting ? 'Deleting...' : 'Delete permanently'}</button></div>
                    </div>
                </div>
            )}

            {showModal && (
                <AddPracticeSetModal
                    onClose={() => setShowModal(false)}
                    onSubmit={handleSubmit}
                    onGenerate={handleGenerate}
                    isSubmitting={isCreating}
                    isGenerating={isGenerating}
                />
            )}
            {showModal && (createError || generateError) && (
                <div className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 rounded-lg border border-red-500/30 bg-red-950 px-4 py-2 text-sm text-red-200 shadow-xl">
                    {generateError ? 'Practice could not be generated. Please adjust the options and try again.' : 'Practice set could not be saved. Please check the form and try again.'}
                </div>
            )}
        </div>
    );
}
